import React, { useState, useEffect } from 'react';
import styles from './FilePreviewModal.module.css';
import { adminApiService } from '../../services/adminApiService';

interface FilePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  fileUrl: string;
  fileName: string;
  contentType?: string;
}

export const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
  isOpen,
  onClose,
  fileUrl,
  fileName,
  contentType
}) => {
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<number>(0);

  const handleDownload = async () => {
    try {
      const blob = await adminApiService.getFileBlobByUrl(fileUrl);
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = fileName || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    } catch (e) {
      setError('Failed to download file');
    }
  };

  useEffect(() => {
    if (!isOpen || !fileUrl) return;

    console.log('FilePreviewModal - Opening file:', {
      fileUrl,
      fileName,
      contentType,
      isOpen
    });

    const fetchContent = async () => {
      setLoading(true);
      setError(null);
      setProgress(0);
      try {
        // For images, use the URL directly - no need to fetch as blob
        if (contentType?.startsWith('image/')) {
          console.log('Using image URL directly:', fileUrl);
          setContent(fileUrl);
          setLoading(false);
          return;
        }

        // For PDFs, try authenticated fetch via axios instance to include auth headers/cookies
        if (contentType === 'application/pdf') {
          console.log('Loading PDF with URL (auth):', fileUrl);
          try {
            const blob = await adminApiService.getFileBlobByUrl(fileUrl);
            const objectUrl = URL.createObjectURL(blob);
            setContent(objectUrl);
            setLoading(false);
            return;
          } catch (err) {
            // Fallback to fetch if axios path fails
            try {
              const response = await fetch(fileUrl, { method: 'GET', credentials: 'include' });
              if (!response.ok) throw new Error(`Failed to access PDF: ${response.status} ${response.statusText}`);
              const blob = await response.blob();
              const objectUrl = URL.createObjectURL(blob);
              setContent(objectUrl);
              setLoading(false);
              return;
            } catch (fallbackErr) {
              throw fallbackErr;
            }
          }
        }

        console.log('Fetching file from URL:', fileUrl);
        
        // Add timeout for other file types too
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
        
        try {
          const response = await fetch(fileUrl, {
            method: 'GET',
            credentials: 'include',
            signal: controller.signal
          });
          
          clearTimeout(timeoutId);
        
          console.log('Fetch response:', {
            status: response.status,
            statusText: response.statusText,
            headers: Object.fromEntries(response.headers.entries())
          });
          
          if (!response.ok) {
            throw new Error(`Failed to access file: ${response.status} ${response.statusText}`);
          }
          
          const blob = await response.blob();
          console.log('Blob created:', { size: blob.size, type: blob.type });

          // For text files, read as text
          if (contentType?.startsWith('text/') || contentType === 'application/json') {
            const text = await blob.text();
            setContent(text);
            return;
          }

          // For other types, show download prompt
          setContent('binary');
        } catch (error) {
          clearTimeout(timeoutId);
          if (error instanceof Error && error.name === 'AbortError') {
            throw new Error('Request timed out. The file might be too large or the server is slow.');
          }
          throw error;
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load file content';
        setError(errorMessage);
        console.error('Error loading file:', {
          fileUrl,
          fileName,
          contentType,
          error: err
        });
      } finally {
        setLoading(false);
      }
    };

    fetchContent();

    // Cleanup function to revoke object URLs (only for blob URLs, not direct URLs)
    return () => {
      if (content && !content.startsWith('http') && content !== 'binary') {
        URL.revokeObjectURL(content);
      }
    };
  }, [isOpen, fileUrl, contentType]);

  if (!isOpen) return null;

  const renderContent = () => {
    if (loading) {
      return (
        <div className={styles.loading}>
          <div>Loading file...</div>
          {progress > 0 && (
            <div style={{ marginTop: '10px', width: '100%', backgroundColor: '#f0f0f0', borderRadius: '4px', overflow: 'hidden' }}>
              <div 
                style={{ 
                  width: `${progress}%`, 
                  height: '20px', 
                  backgroundColor: '#3b82f6', 
                  transition: 'width 0.3s ease' 
                }}
              />
            </div>
          )}
        </div>
      );
    }

    if (error) {
      return (
        <div className={styles.error}>
          <p>{error}</p>
          {error.includes('timed out') && (
            <div style={{ marginTop: '10px' }}>
              <p>For large files, try:</p>
              <ul style={{ textAlign: 'left', margin: '10px 0' }}>
                <li>Downloading the file directly using the download button below</li>
                <li>Checking your internet connection</li>
                <li>Refreshing the page and trying again</li>
              </ul>
            </div>
          )}
        </div>
      );
    }

    if (!content) {
      return <div className={styles.error}>No content available</div>;
    }

    if (contentType?.startsWith('image/')) {
      return <img src={content} alt={fileName} className={styles.image} />;
    }

    if (contentType === 'application/pdf') {
      return (
        <div className={styles.pdfContainer}>
          <object
            data={`${content}#toolbar=0`}
            type="application/pdf"
            className={styles.pdf}
          >
            <p>Unable to display PDF. <a href={content} target="_blank" rel="noopener noreferrer">Open in new tab</a></p>
          </object>
        </div>
      );
    }

    if (content === 'binary') {
      return (
        <div className={styles.binaryFile}>
          <p>This file type cannot be previewed.</p>
          <button onClick={handleDownload} className={styles.downloadButton}>Download File</button>
        </div>
      );
    }

    // Text content
    return <pre className={styles.textContent}>{content}</pre>;
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>{fileName}</h3>
          <button className={styles.closeButton} onClick={onClose}>×</button>
        </div>
        <div className={styles.modalBody}>
          {renderContent()}
        </div>
        <div className={styles.modalFooter}>
          <button onClick={handleDownload} className={styles.downloadButton}>Download</button>
          <button onClick={onClose} className={styles.closeButton}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};